import type { SVGProps } from "react";
import * as Icons from "@/components/ui/atoms/icons";
import { findToolkitByName } from "./utils";

export type ToolkitCatalogueEntry = {
  label: string;
};

// Mapping of toolkit IDs to their icon components
export const TOOLKIT_ICON_MAP: Record<
  string,
  (props: SVGProps<SVGSVGElement>) => React.JSX.Element
> = {
  ADPWorkforceNow: Icons.Adp,
  Aha: Icons.Aha,
  Airtable: Icons.Airtable,
  AirtableApi: Icons.Airtable,
  Amplitude: Icons.Amplitude,
  Apollo: Icons.Apollo,
  ArcadeEngineApi: Icons.Arcade,
  Asana: Icons.Asana,
  Ashby: Icons.Ashby,
  AshbyApi: Icons.Ashby,
  Attio: Icons.Attio,
  Auth0: Icons.Auth0,
  BambooHR: Icons.Bamboohr,
  Basecamp: Icons.Basecamp,
  BillCom: Icons.Bill,
  Bitbucket: Icons.Bitbucket,
  Bluesky: Icons.Bluesky,
  Box: Icons.Box,
  BoxApi: Icons.Box,
  Braze: Icons.Braze,
  Brex: Icons.Brex,
  Brightdata: Icons.Brightdata,
  Buffer: Icons.Buffer,
  Calendly: Icons.Calendly,
  CalendlyApi: Icons.Calendly,
  Clickhouse: Icons.Clickhouse,
  ClickUp: Icons.Clickup,
  CloseIO: Icons.Closeio,
  Codesandbox: Icons.Codesandbox,
  Coinbase: Icons.Coinbase,
  Confluence: Icons.Confluence,
  CursorAgents: Icons.Cursor,
  CursorAgentsApi: Icons.Cursor,
  Customerio: Icons.Customerio,
  CustomerioApi: Icons.Customerio,
  CustomerioPipelinesApi: Icons.Customerio,
  CustomerioTrackApi: Icons.Customerio,
  Datadog: Icons.Datadog,
  DatadogApi: Icons.Datadog,
  Daytona: Icons.Daytona,
  DigitalOcean: Icons.DigitalOcean,
  Discord: Icons.Discord,
  DiscordBot: Icons.Discord,
  Dropbox: Icons.Dropbox,
  E2b: Icons.E2b,
  EBay: Icons.Ebay,
  Evernote: Icons.Evernote,
  ExaApi: Icons.Exa,
  Factorial: Icons.Factorial,
  Figma: Icons.Figma,
  FigmaApi: Icons.Figma,
  Firecrawl: Icons.Firecrawl,
  Fireflies: Icons.Fireflies,
  FlyIo: Icons.FlyIo,
  Forkable: Icons.Forkable,
  Freshdesk: Icons.Freshdesk,
  FreshserviceApi: Icons.Freshservice,
  Github: Icons.Github,
  GitLab: Icons.Gitlab,
  Glean: Icons.Glean,
  Gmail: Icons.Gmail,
  GoogleCalendar: Icons.GoogleCalendar,
  GoogleContacts: Icons.GoogleContacts,
  GoogleDocs: Icons.GoogleDocs,
  GoogleDrive: Icons.GoogleDrive,
  GoogleFinance: Icons.GoogleFinance,
  GoogleFlights: Icons.GoogleFlights,
  GoogleForms: Icons.GoogleForms,
  GoogleHotels: Icons.GoogleHotels,
  GoogleJobs: Icons.GoogleJobs,
  GoogleMaps: Icons.GoogleMaps,
  GoogleNews: Icons.GoogleNews,
  GoogleSearch: Icons.Google,
  GoogleSheets: Icons.GoogleSheets,
  GoogleShopping: Icons.GoogleShopping,
  GoogleSlides: Icons.GoogleSlides,
  Granola: Icons.Granola,
  Heroku: Icons.Heroku,
  Hootsuite: Icons.Hootsuite,
  Hubspot: Icons.Hubspot,
  HubspotAutomationApi: Icons.Hubspot,
  HubspotCmsApi: Icons.Hubspot,
  HubspotConversationsApi: Icons.Hubspot,
  HubspotCrmApi: Icons.Hubspot,
  HubspotEventsApi: Icons.Hubspot,
  HubspotMarketingApi: Icons.Hubspot,
  HubspotMeetingsApi: Icons.Hubspot,
  HubspotUsersApi: Icons.Hubspot,
  Imgflip: Icons.Imgflip,
  Insightly: Icons.Insightly,
  IntercomApi: Icons.Intercom,
  Jira: Icons.Jira,
  Linear: Icons.Linear,
  Linkedin: Icons.Linkedin,
  LumaApi: Icons.Luma,
  Mailchimp: Icons.Mailchimp,
  MailchimpMarketingApi: Icons.Mailchimp,
  Math: Icons.MathToolkit,
  MCP: Icons.Mcp,
  MicrosoftDynamics: Icons.MicrosoftDynamics,
  MicrosoftExcel: Icons.MicrosoftExcel,
  MicrosoftOutlookCalendar: Icons.MicrosoftOutlookCalendar,
  MicrosoftOutlookMail: Icons.MicrosoftOutlookMail,
  MicrosoftPowerBI: Icons.MicrosoftPowerBi,
  MicrosoftPowerpoint: Icons.MicrosoftPowerpoint,
  MicrosoftSharePoint: Icons.MicrosoftSharepoint,
  Sharepoint: Icons.MicrosoftSharepoint,
  MicrosoftTeams: Icons.MicrosoftTeams,
  MicrosoftWord: Icons.MicrosoftWord,
  Miro: Icons.Miro,
  MiroApi: Icons.Miro,
  Monday: Icons.Monday,
  MongoDB: Icons.MongoDB,
  Netsuite: Icons.Netsuite,
  Notion: Icons.Notion,
  NotionToolkit: Icons.Notion,
  Obsidian: Icons.Obsidian,
  Okta: Icons.Okta,
  MicrosoftOnedrive: Icons.MicrosoftOnedrive,
  OutlookCalendar: Icons.MicrosoftOutlookCalendar,
  OutlookMail: Icons.MicrosoftOutlookMail,
  PagerDuty: Icons.Pagerduty,
  PagerDutyApi: Icons.Pagerduty,
  Pinecone: Icons.Pinecone,
  Pinterest: Icons.Pinterest,
  Pipedrive: Icons.Pipedrive,
  Plaid: Icons.Plaid,
  Posthog: Icons.Posthog,
  PosthogApi: Icons.Posthog,
  Postgres: Icons.Postgres,
  Postman: Icons.Postman,
  Pylon: Icons.Pylon,
  PylonApi: Icons.Pylon,
  Quickbooks: Icons.Quickbooks,
  Railway: Icons.Railway,
  Reddit: Icons.Reddit,
  Resend: Icons.Resend,
  Salesforce: Icons.Salesforce,
  Shopify: Icons.Shopify,
  Singlestore: Icons.Singlestore,
  Slack: Icons.Slack,
  SlackApi: Icons.Slack,
  Snowflake: Icons.Snowflake,
  Splunk: Icons.Splunk,
  Spotify: Icons.Spotify,
  Square: Icons.Square,
  Squareup: Icons.Squareup,
  SquareupApi: Icons.Square,
  SquareUpApi: Icons.Square,
  Squarespace: Icons.Squarespace,
  Stripe: Icons.Stripe,
  StripeApi: Icons.Stripe,
  Telegram: Icons.Telegram,
  Tiktok: Icons.Tiktok,
  TickTickApi: Icons.Ticktick,
  TrelloApi: Icons.Trello,
  Twilio: Icons.Twilio,
  Twitch: Icons.Twitch,
  Vercel: Icons.Vercel,
  Walmart: Icons.Walmart,
  WeaviateApi: Icons.Weaviate,
  Workday: Icons.Workday,
  Wrike: Icons.Wrike,
  X: Icons.X,
  Xero: Icons.Xero,
  XeroApi: Icons.Xero,
  YugabyteDB: Icons.Yugabytedb,
  ZohoBooksApi: Icons.ZohoBooks,
  ZohoCreatorApi: Icons.ZohoCreator,
  Youtube: Icons.Youtube,
  Zendesk: Icons.Zendesk,
  Zoom: Icons.Zoom,
};

// Lowercased index of TOOLKIT_ICON_MAP, built once so every call is a single
// Map.get — no per-call .find() + .toLowerCase() over ~170 keys. A Map keeps
// the lookup table outside the prototype chain, so a malformed toolkit id
// (e.g. "__proto__", "constructor") can't accidentally return a built-in.
const TOOLKIT_ICON_INDEX = new Map<
  string,
  (props: SVGProps<SVGSVGElement>) => React.JSX.Element
>(
  Object.entries(TOOLKIT_ICON_MAP).map(([id, icon]) => [id.toLowerCase(), icon])
);

/**
 * Resolve a toolkit ID to its brand icon in a single lookup, reporting whether
 * the result is a real brand mark or the `Generic` placeholder. Prefer this
 * over calling `hasToolkitIcon` + `getToolkitIcon` separately.
 */
export const resolveToolkitIcon = (
  toolkitId: string | undefined
): {
  Icon: (props: SVGProps<SVGSVGElement>) => React.JSX.Element;
  hasBrandIcon: boolean;
} => {
  const match = toolkitId
    ? TOOLKIT_ICON_INDEX.get(toolkitId.toLowerCase())
    : undefined;
  return { Icon: match ?? Icons.Generic, hasBrandIcon: Boolean(match) };
};

/**
 * Get toolkit icon component by toolkit ID
 * @param toolkitId - The toolkit ID (e.g., 'Gmail', 'SlackAPI', 'GoogleCalendar')
 * @returns Icon component function, or Generic icon if not found
 */
export const getToolkitIcon = (
  toolkitId: string | undefined
): ((props: SVGProps<SVGSVGElement>) => React.JSX.Element) =>
  resolveToolkitIcon(toolkitId).Icon;

/**
 * Whether a toolkit ID has a real brand icon registered, so callers can
 * decide whether to render the brand mark or a typographic fallback (e.g. a
 * one-letter avatar) instead of the Generic placeholder.
 */
export const hasToolkitIcon = (toolkitId: string | undefined): boolean =>
  resolveToolkitIcon(toolkitId).hasBrandIcon;

/**
 * Get a toolkit icon component by toolkit name (converts name to ID first)
 * @param toolkitName - The toolkit name (e.g., 'Gmail', 'Slack')
 * @returns Icon component wrapped for className support
 */
export const getToolkitIconByName = (
  toolkitName: string
): ((props: { className?: string }) => React.ReactElement) => {
  // Look up toolkit ID from name
  const toolkit = findToolkitByName(toolkitName);

  const IconComponent = getToolkitIcon(toolkit?.id);
  return ({ className }: { className?: string }) => (
    <IconComponent className={className} />
  );
};

/**
 * Get a tool icon component by toolkit name (converts name to ID first)
 * @param toolkitName - The toolkit name
 * @returns Icon component wrapped for className support
 */
export const getToolIconByToolkitName = (
  toolkitName: string
): ((props: { className?: string }) => React.ReactElement) => {
  // Look up toolkit ID from name
  const toolkit = findToolkitByName(toolkitName);

  const IconComponent = getToolkitIcon(toolkit?.id);
  return ({ className }: { className?: string }) => (
    <IconComponent className={className} />
  );
};
